import lodash from 'lodash'
import dateUtil from '@/utils/dateUtil'
import i18n from '@/utils/i18n'
import store from '@/store'
import { WORK_PERMIT_EXTENDED, WORK_PERMIT_STATUS, WORK_PERMIT_TYPES } from './consts'
import {
  buildBodyOnlyLifecycleCommentResult,
  buildLifecycleTableCommentResult,
  hasVisibleHistoryText,
  WP_LIFECYCLE_COMMENT_HTML_KEY,
} from './lifecycleTableCommentDisplay'

export { WP_LIFECYCLE_COMMENT_HTML_KEY }

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Format Work Permit histories for Lifecycle modal comment display.
 * @param {Array} histories
 * @returns {Array}
 */
export function formatWorkPermitHistories (histories) {
  return histories.map(formatWorkPermitHistory)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Prepare cloned Work Permit histories for the current Lifecycle modal viewer.
 * @param {Array} histories
 * @param {Object} [options]
 * @param {boolean} [options.isEngineer]
 * @param {boolean} [options.formatComments]
 * @returns {Array}
 */
export function formatWorkPermitHistoriesForViewer (
  histories,
  {
    isEngineer = false,
    formatComments = true,
    permitType,
  } = {},
) {
  const rawHistories = lodash.cloneDeep(histories || [])
    .filter(item => getHistoryRowStatus(item) !== 'UPDATE_WORKERS')
  if (permitType) {
    rawHistories.forEach(item => {
      if (item && getHistoryRowType(item) == null) {
        item.type = permitType
      }
    })
  }
  const commentsFormattedHistories = formatComments
    ? formatWorkPermitHistories(rawHistories)
    : rawHistories
  if (isEngineer) return commentsFormattedHistories
  return commentsFormattedHistories.filter(canShowExternalLifecycleHistory)
}

/**
 * author: alvis
 * timestamp: 2026-05-11
 * description: Use backend lifecycle visibility decisions for external viewer rows.
 * @param {Object} item - Work Permit history row.
 * @returns {boolean}
 */
function canShowExternalLifecycleHistory (item) {
  return item && item.externalViewerLifecycleVisible === true
}

const WORK_PERMIT_HISTORY_COMMENT_CLEAR_STATUSES = [
  WORK_PERMIT_STATUS.COMPLETED,
  WORK_PERMIT_STATUS.INPROGRESS,
]

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Read history row status from top-level or Sequelize-style dataValues.
 * @param {Object} row
 * @returns {*}
 */
function getHistoryRowStatus (row) {
  if (row == null || typeof row !== 'object') {
    return undefined
  }
  if (row.status != null && row.status !== '') {
    return row.status
  }
  if (row.dataValues && row.dataValues.status != null && row.dataValues.status !== '') {
    return row.dataValues.status
  }
  return undefined
}

/**
 * author: alvis
 * timestamp: 2026-05-21
 * description: Read history row permit type from top-level or Sequelize-style dataValues.
 * @param {Object} row
 * @returns {*}
 */
function getHistoryRowType (row) {
  if (row == null || typeof row !== 'object') {
    return undefined
  }
  if (row.type != null && row.type !== '') {
    return row.type
  }
  if (row.dataValues && row.dataValues.type != null && row.dataValues.type !== '') {
    return row.dataValues.type
  }
  return undefined
}

function isHoistingHistoryRow (row) {
  return getHistoryRowType(row) === WORK_PERMIT_TYPES.HOISTING
}

/**
 * author: alvis
 * timestamp: 2026-05-11
 * description: Match server loose JSON for `detail.isResume` (resume audit rows).
 * @param {Object} [detail]
 * @returns {boolean}
 */
function isResumeHistoryDetailFlagLoose (detail) {
  if (!detail || typeof detail !== 'object') return false
  const v = detail.isResume
  return v === true || v === 'true' || v === 1 || v === '1'
}

/**
 * author: alvis
 * timestamp: 2026-05-22
 * description: Match server loose JSON for `detail.isReassign` (reviewer reassignment audit rows).
 * @param {Object} [detail]
 * @returns {boolean}
 */
function isReassignHistoryDetailFlagLoose (detail) {
  if (!detail || typeof detail !== 'object') return false
  const v = detail.isReassign
  return v === true || v === 'true' || v === 1 || v === '1'
}

/**
 * author: alvis
 * timestamp: 2026-05-11
 * description: WAPPR resume rows normalize internal `comments` to sole-external shape; Lifecycle reads `comments` only — merge visible line for plain + optional engineer HTML.
 * @param {Object} item
 * @returns {Object}
 */
function applyWapprResumeLifecycleComment (item) {
  if (!item || !item.detail || !isResumeHistoryDetailFlagLoose(item.detail)) {
    return item
  }
  const {
    intTrim,
    extTrim,
  } = getTrimmedHistoryCommentDetail(item.detail)
  const plain = hasVisibleHistoryText(intTrim) ? intTrim : extTrim
  if (!hasVisibleHistoryText(plain)) {
    return item
  }
  const result = buildBodyOnlyLifecycleCommentResult(plain, WP_LIFECYCLE_COMMENT_HTML_KEY)
  if (result == null) {
    return item
  }
  lodash.set(item, 'detail.comments', result.comments)
  if (result.htmlByKey) {
    Object.entries(result.htmlByKey).forEach(([k, v]) => {
      lodash.set(item, `detail.${k}`, v)
    })
  }
  return item
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Format one Work Permit history row for Lifecycle modal display.
 * @param {Object} item
 * @returns {Object}
 */
function formatWorkPermitHistory (item) {
  if (!item) return item
  const st = getHistoryRowStatus(item)
  if (st === WORK_PERMIT_STATUS.SUSPENDED) {
    return formatWorkPermitSuspendedHistoryComments(item)
  }
  if (WORK_PERMIT_HISTORY_COMMENT_CLEAR_STATUSES.includes(st)) {
    return clearCommentsInfo(item)
  }
  if (st === WORK_PERMIT_STATUS.WAPPR) {
    return applyWapprResumeLifecycleComment(item)
  }
  return formatWorkPermitLifecycleHistoryComment(item)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Apply extension or lifecycle comment formatting to one history row.
 * @param {Object} item
 * @returns {Object}
 */
function formatWorkPermitLifecycleHistoryComment (item) {
  if (isWorkPermitExtensionHistory(item)) {
    return formatWorkPermitExtensionComments(item)
  }
  return applyWorkPermitLifecycleTableComments(item)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Check whether the history row represents a Work Permit extension action.
 * @param {Object} item
 * @returns {boolean}
 */
function isWorkPermitExtensionHistory (item) {
  return [
    lodash.get(item, 'detail.comments'),
    lodash.get(item, 'detail.externalComments'),
  ].some(comment => String(comment || '').trim() === WORK_PERMIT_EXTENDED)
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Trim internal and external comment fields from a history detail object.
 * @param {Object} [detail]
 * @returns {{intTrim: string, extTrim: string}}
 */
function getTrimmedHistoryCommentDetail (detail = {}) {
  return {
    intTrim: detail.comments != null ? String(detail.comments).trim() : '',
    extTrim: detail.externalComments != null ? String(detail.externalComments).trim() : '',
  }
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Apply Lifecycle table comment formatting to a supported history row.
 * @param {Object} item
 * @returns {Object}
 */
function applyWorkPermitLifecycleTableComments (item) {
  const st = getHistoryRowStatus(item)
  if (!item || !item.detail || !st) {
    return item
  }
  const {
    intTrim,
    extTrim,
  } = getTrimmedHistoryCommentDetail(item.detail)
  const result = buildLifecycleTableCommentResult({
    status: st,
    isEngineer: store.getters['user/isEngineer'],
    intTrim,
    extTrim,
    internalLabel: i18n.t('workPermit.internalCommentsHistoryLabel'),
    externalLabel: i18n.t('workPermit.externalCommentsHistoryLabel'),
    htmlKey: WP_LIFECYCLE_COMMENT_HTML_KEY,
    bodyOnly: isResumeHistoryDetailFlagLoose(item.detail) ||
      isReassignHistoryDetailFlagLoose(item.detail) ||
      isHoistingHistoryRow(item),
  })
  if (result == null) {
    return item
  }
  lodash.set(item, 'detail.comments', result.comments)
  if (result.htmlByKey) {
    Object.entries(result.htmlByKey).forEach(([k, v]) => {
      lodash.set(item, `detail.${k}`, v)
    })
  }
  return item
}

/**
 * author: alvis
 * timestamp: 2026-05-15
 * description: Resolve the request-extend date label for extension-reject rows; avoids moment fallback errors on blank/invalid API values.
 * @param {*} rawReq
 * @returns {string}
 */
function requestExtendDateDisplayLabel (rawReq) {
  if (rawReq == null) return i18n.t('emptyValue')
  if (typeof rawReq === 'string' && rawReq.trim() === '') return i18n.t('emptyValue')
  const formatted = dateUtil.format(rawReq, dateUtil.DD_MMM_YY_HH_MM)
  return formatted != null && String(formatted).trim() !== '' ? formatted : i18n.t('emptyValue')
}

/**
 * author: alvis
 * timestamp: 2026-06-26
 * description: Resolve the target date from legacy and current extension history shapes.
 * @param {Object} item
 * @returns {*}
 */
function getWorkPermitExtensionTargetDate (item) {
  const nestedEndDate = lodash.get(item, 'detail.extendDate.endDate')
  if (nestedEndDate) return nestedEndDate

  const directEndDate = lodash.get(item, 'detail.endDate')
  if (directEndDate) return directEndDate

  const scalarExtendDate = lodash.get(item, 'detail.extendDate')
  if (scalarExtendDate && typeof scalarExtendDate !== 'object') return scalarExtendDate

  return null
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Format Work Permit extension comments for Lifecycle modal rows.
 * @param {Object} item
 * @returns {Object}
 */
function formatWorkPermitExtensionComments (item) {
  const endDate = getWorkPermitExtensionTargetDate(item)
  const rejectReason = lodash.get(item, 'detail.rejectReason')
  if (endDate) {
    const formattedComments = i18n.t('workPermit.extensionComment', [dateUtil.format(new Date(endDate), dateUtil.DD_MMM_YY_HH_MM)])
    lodash.set(item, 'detail.comments', formattedComments)
  }
  if (rejectReason) {
    const formattedComment = i18n.t('workPermit.rejectExtensionComment', [
      requestExtendDateDisplayLabel(lodash.get(item, 'detail.requestExtendDate')),
      rejectReason,
    ])
    lodash.set(item, 'detail.comments', formattedComment)
  }
  return item
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Format suspended Work Permit comments with the Remarks label.
 * @param {Object} item
 * @returns {Object}
 */
function formatWorkPermitSuspendedHistoryComments (item) {
  const suspendEffectiveDate = item.detail && item.detail.suspendEffectiveDate
  const originalComments = (item.detail && item.detail.comments) || ''
  const formattedComments = suspendEffectiveDate
    ? `${i18n.t('workPermit.remarks')}: ${originalComments || i18n.t('emptyValue')} `
    : originalComments
  lodash.set(item, 'detail.comments', formattedComments)
  return item
}

/**
 * author: alvis
 * timestamp: 2026-05-07
 * description: Clear Lifecycle comments for statuses whose content is shown elsewhere.
 * @param {Object} item
 * @returns {Object}
 */
function clearCommentsInfo (item) {
  item.detail.comments = null
  return item
}
